/**
 * The Research browse grid — lays out per-entity-type cards plus the
 * Documents and Mentions cards in a 1/2/3-column responsive grid.
 *
 * Renders in both browse mode (no query) and search mode (with query).
 * Header is intentionally tiny — in browse mode it's omitted entirely so
 * the grid is the page. In search mode a single whisper-line declares
 * "N matches across M types" so the user knows the grid was filtered.
 */
import { FlaskConical, Loader2, Search as SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import type { BrowseStatus } from '@/features/search/hooks/use-browse-extracted'
import type { EntityHit, SearchContentResponse } from '@/services/api/case-search'
import { DocumentsCard } from './DocumentsCard'
import { EntityGroupCard } from './EntityGroupCard'
import { MentionsCard } from './MentionsCard'

interface Props {
  caseId: string
  results: SearchContentResponse | null
  query: string
  status: BrowseStatus
  onAskAgent?: () => void
  asking?: boolean
  onSelectEntity?: (hit: EntityHit) => void
  /** The results are one document's: their rows do not name it, and an empty answer speaks of it. */
  documentFiltered: boolean
}

export function GroupedBrowse({
  caseId,
  results,
  query,
  status,
  onAskAgent,
  asking,
  onSelectEntity,
  documentFiltered,
}: Props) {
  const { t } = useTranslation()

  if (status === 'loading' && !results) {
    return (
      <div className="flex items-center justify-center px-6 py-16 text-sm text-muted-foreground/80">
        <Loader2 className="mr-2 size-3.5 animate-spin" />
        {t('research.loading')}
      </div>
    )
  }
  if (status === 'error') {
    return (
      <Card tone="destructive" size="compact" className="px-4 py-3 text-sm text-destructive">
        {t('research.error')}
      </Card>
    )
  }

  const groups = results?.groups ?? []
  const documents = results?.documents ?? []
  const chunks = results?.chunks ?? []
  const everythingEmpty = groups.length === 0 && documents.length === 0 && chunks.length === 0

  if (!query && everythingEmpty) return <EmptyBrowseState documentFiltered={documentFiltered} />
  if (query && everythingEmpty) {
    return <NoMatchesState query={query} onAskAgent={onAskAgent} asking={asking} />
  }

  return (
    <section className="space-y-3">
      {query && <SearchMeta matches={countMatches(groups)} types={groups.length} />}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {groups.map(group => (
          <EntityGroupCard
            key={group.type}
            caseId={caseId}
            group={group}
            query={query || undefined}
            onSelectEntity={onSelectEntity}
            hideSource={documentFiltered}
          />
        ))}
        <DocumentsCard caseId={caseId} documents={documents} query={query || undefined} />
        <MentionsCard caseId={caseId} chunks={chunks} query={query || undefined} />
      </div>
    </section>
  )
}

/** What the groups hold, not the previews they show. */
function countMatches(groups: SearchContentResponse['groups']): number {
  return groups.reduce((acc, g) => acc + g.total, 0)
}

function SearchMeta({ matches, types }: { matches: number; types: number }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-baseline gap-2 px-0.5 text-2xs text-muted-foreground">
      <span className="font-semibold uppercase tracking-[0.12em] text-foreground/70">
        {t('research.header.matchesLabel')}
      </span>
      <span className="tabular-nums">
        {t('research.header.matchesSummary', { matches, types })}
      </span>
    </div>
  )
}

function EmptyBrowseState({ documentFiltered }: { documentFiltered: boolean }) {
  const { t } = useTranslation()
  return (
    <Card variant="dashed" className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <SearchIcon className="size-4" />
      </div>
      <div className="space-y-1">
        <div className="text-sm font-medium text-foreground">
          {t(
            documentFiltered
              ? 'research.empty.noDocumentEntitiesTitle'
              : 'research.empty.noExtractionTitle'
          )}
        </div>
        <div className="max-w-sm text-xs text-muted-foreground">
          {t(
            documentFiltered
              ? 'research.empty.noDocumentEntitiesHint'
              : 'research.empty.noExtractionHint'
          )}
        </div>
      </div>
    </Card>
  )
}

function NoMatchesState({
  query,
  onAskAgent,
  asking,
}: {
  query: string
  onAskAgent?: () => void
  asking?: boolean
}) {
  const { t } = useTranslation()
  return (
    <Card variant="dashed" className="flex flex-col items-center gap-4 px-6 py-12 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <SearchIcon className="size-4" />
      </div>
      {/* max-w-full + wrap-anywhere: a pasted hash / wallet address has no break
          points and would otherwise keep its full width (min-content). */}
      <div className="max-w-full space-y-1">
        <div className="wrap-anywhere text-sm font-medium text-foreground">
          {t('research.empty.noMatchesTitle', { query })}
        </div>
        <div className="max-w-sm text-xs text-muted-foreground">
          {t('research.empty.noMatchesHint')}
        </div>
      </div>
      {onAskAgent && (
        <button
          type="button"
          onClick={onAskAgent}
          disabled={asking}
          className="inline-flex max-w-full items-center gap-1.5 wrap-anywhere rounded-xl bg-primary px-4 py-2.5 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-30 md:py-2"
        >
          {asking ? (
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
          ) : (
            <FlaskConical className="size-3.5 shrink-0" />
          )}
          <span className="min-w-0">{t('research.empty.askAgentCta', { query })}</span>
        </button>
      )}
    </Card>
  )
}
